import React, { useState } from 'react';
import {
  useGetList,
  useGetIdentity,
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Paper,
  Box,
  CircularProgress,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import { Aftercare, AftercareCall1Status } from '../types';

interface Props {
  funeralId: number;
  label?: string;
}

const statusLabels: Record<AftercareCall1Status, string> = {
  [AftercareCall1Status.PENDING]: 'Pending',
  [AftercareCall1Status.COMPLETED]: 'Done',
  [AftercareCall1Status.DECLINED]: 'Declined',
};

const pillStyle = (status: AftercareCall1Status): { bg: string; hover: string; color: string } => {
  switch (status) {
    case AftercareCall1Status.COMPLETED:
      return { bg: 'success.light', hover: 'success.main', color: 'white' };
    case AftercareCall1Status.DECLINED:
      return { bg: '#ffb74d', hover: '#ff9800', color: 'white' };
    default:
      return { bg: 'grey.100', hover: 'grey.200', color: 'text.primary' };
  }
};

export const AftercareCall1Field = ({ funeralId }: Props) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<AftercareCall1Status | null>(null);

  const { data, isLoading, refetch } = useGetList<Aftercare>(
    'aftercare',
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'DESC' },
      filter: { funeral_id: funeralId },
    },
    { enabled: !!funeralId },
  );

  const aftercare = data?.[0];
  const currentStatus = (aftercare?.call_1_status as AftercareCall1Status) || AftercareCall1Status.PENDING;

  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setPendingSelection(null);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!pendingSelection || pendingSelection === currentStatus) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      const nowIso = new Date().toISOString();
      const resolving = pendingSelection !== AftercareCall1Status.PENDING;
      const userId = resolving && identity?.id ? Number(identity.id) : null;
      const userName = resolving ? identity?.fullName || 'System' : null;

      const updates = {
        call_1_status: pendingSelection,
        call_1_completed_at: resolving ? nowIso : null,
        call_1_completed_by_user_id: userId,
        call_1_completed_by_user_name: userName,
      };

      if (aftercare?.id) {
        await dataProvider.update('aftercare', {
          id: aftercare.id,
          data: updates,
          previousData: aftercare,
        });
      } else {
        await dataProvider.create('aftercare', {
          data: {
            funeral_id: funeralId,
            email_enabled: true,
            ...updates,
          },
        });
      }

      notify(`Call 1 marked ${statusLabels[pendingSelection]}`, { type: 'success' });
      await refetch();
      refresh();
      setOpen(false);
    } catch (err: any) {
      notify(err.message || 'Failed to update Call 1 status', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          display: 'inline-flex',
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: 'grey.100',
        }}
      >
        <CircularProgress size={16} />
      </Paper>
    );
  }

  const style = pillStyle(currentStatus);

  return (
    <>
      <Paper
        onClick={handleClick}
        elevation={0}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          padding: '4px 8px',
          borderRadius: '4px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          backgroundColor: style.bg,
          color: style.color,
          '&:hover': {
            backgroundColor: style.hover,
            transform: 'translateY(-1px)',
            boxShadow: 1,
          },
        }}
      >
        {currentStatus === AftercareCall1Status.COMPLETED && <CheckIcon fontSize="small" />}
        {currentStatus === AftercareCall1Status.DECLINED && <BlockIcon fontSize="small" />}
        <Typography variant="body2" sx={{ color: style.color, fontWeight: 500 }}>
          {statusLabels[currentStatus]}
        </Typography>
      </Paper>
      <div onClick={stopPropagation}>
        <Dialog open={open} onClose={() => setOpen(false)} onClick={stopPropagation} maxWidth="xs" fullWidth>
          <DialogTitle>Aftercare Call 1</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ my: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Current status: <strong>{statusLabels[currentStatus]}</strong>
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {Object.values(AftercareCall1Status).map((status) => {
                  const s = pillStyle(status);
                  const selected = (pendingSelection ?? currentStatus) === status;
                  return (
                    <Button
                      key={status}
                      variant={selected ? 'contained' : 'outlined'}
                      onClick={() => setPendingSelection(status)}
                      disabled={saving}
                      sx={{
                        backgroundColor: selected ? s.bg : 'transparent',
                        color: selected ? s.color : 'text.primary',
                        borderColor: s.bg,
                        '&:hover': {
                          backgroundColor: s.hover,
                          color: s.color,
                          borderColor: s.hover,
                        },
                      }}
                    >
                      {statusLabels[status]}
                    </Button>
                  );
                })}
              </Box>
              {currentStatus !== AftercareCall1Status.PENDING && aftercare?.call_1_completed_at && (
                <Typography variant="caption" color="text.secondary">
                  Last updated{' '}
                  {new Date(aftercare.call_1_completed_at).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                  {aftercare.call_1_completed_by_user_name
                    ? ` by ${aftercare.call_1_completed_by_user_name}`
                    : ''}
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !pendingSelection || pendingSelection === currentStatus}
            >
              {saving ? 'Saving…' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};
